import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { LanguageChanged } from '../actions';
import { selectCatName, selectCatalog } from '../catalog-store/selectors';
import { RootState } from '../root-state.model';
import { NavigateTo } from './actions';
import { selectRouteTranslationKey } from './selectors';

@Injectable()
export class RouterEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly localeService = inject(LocaleService);

  setTitlePageTitle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED, LanguageChanged),
        withLatestFrom(
          this.store.select(selectRouteTranslationKey).pipe(filter((k) => !!k)),
          this.store.select(selectCatalog).pipe(filter((c) => !!c.client)),
        ),
        switchMap(([, key, catalog]) =>
          this.translate.get([key, 'RESERVATION_SYSTEM'], { catName: catalog.name }).pipe(
            map((translations) => ({
              keyTranslated: translations[key],
              catalog,
              reservationSystem: translations['RESERVATION_SYSTEM'],
            })),
          ),
        ),
        tap((r) =>
          this.title.setTitle(
            `${r.keyTranslated} - ${r.catalog.client.organization} - 2reserve ${r.reservationSystem}`,
          ),
        ),
      ),
    { dispatch: false },
  );

  navigateToRoute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavigateTo),
        withLatestFrom(this.store.select(selectCatName)),
        tap(([action, catName]) => {
          switch (action.route) {
            case 'Home':
              this.router.navigate([catName, this.localeService.currentLang()], {
                queryParams: action.queryParams,
              });
              break;
            case 'Cart':
              this.router.navigate([catName, this.localeService.currentLang(), 'cart'], {
                queryParams: action.queryParams,
              });
              break;
            case 'Checkout':
              this.router.navigate([catName, this.localeService.currentLang(), 'checkout']);
              break;
            case 'Login':
              this.router.navigate([catName, this.localeService.currentLang(), 'login'], {
                queryParams: action.queryParams,
              });
              break;
          }
        }),
      ),
    { dispatch: false },
  );
}
